import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import constants from 'helpers/constants';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import Icon from 'components/atoms/Icons/Icon/Icon';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import Button from 'components/atoms/Buttons/Button/Button';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import {Span} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import Close from '@mui/icons-material/Close';
import Save from '@mui/icons-material/Save';
import StyledEntitiesBulkDialog from 'components/organisms/Dialogs/EntitiesBulkDialog/EntitiesBulkDialog.styles';

const EntitiesBulkDialog = React.forwardRef((props, ref) => {
  const {
    title,
    count,
    bulkActions,
    collection,
    universeCollection,
    fieldData,
    onClose,
    onChange,
    onSubmit,
    ...innerProps
  } = useComponentProps(props, 'EntitiesBulkDialog');

  const innerRef = useRef(null);
  const formRef = useRef(null);
  const valueFieldRef = useRef(null);

  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(null);
  const [field, setField] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useImperativeHandle(ref, () => innerRef.current);

  const fields = useMemo(() => {
    const fields = [];
    const fieldOptions = bulkActions.reduce((a, ba) => {
      if ([constants.entity.bulkActions.setClientTags].includes(ba.value)) {
        universeCollection?.tagGroups?.forEach((tagGroup) => {
          a.push({
            label: tagGroup.name,
            value: `${constants.entity.bulkActions.setClientTags}_${tagGroup.groupId}`
          })
        })
      } else if ([constants.entity.bulkActions.setCollectionTags].includes(ba.value)) {
        collection?.tagGroups?.forEach((tagGroup) => {
          a.push({
            label: tagGroup.name,
            value: `${constants.entity.bulkActions.setCollectionTags}_${tagGroup.groupId}`
          })
        })
      } else {
        a.push({
          label: utils.upperFirst(ba.label.replace('Set ', '')),
          value: ba.value
        });
      }

      return a;
    }, []);

    fields.push({
      name: 'field',
      entity: 'bulk',
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.text,
      conversion: constants.formFieldConversionTypes.value,
      options: fieldOptions,
      required: true,
      FormFieldProps: {
        variant: 'staticLabel'
      }
    });

    if (field?.value === constants.entity.bulkActions.setPersonalRelevancy) {
      fields.push({
        name: 'personalRelevancy',
        label: 'Value',
        placeholder: 'Personal relevance',
        entity: 'bulk',
        type: constants.formFieldTypes.autocomplete,
        validation: constants.formFieldValidationTypes.text,
        conversion: constants.formFieldConversionTypes.value,
        options: 'hml',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel',
          ref: valueFieldRef
        }
      });
    } else if (field?.value === constants.entity.bulkActions.setExternalRelevancy) {
      fields.push({
        name: 'externalRelevancy',
        label: 'Value',
        placeholder: 'External relevance',
        entity: 'bulk',
        type: constants.formFieldTypes.autocomplete,
        validation: constants.formFieldValidationTypes.text,
        conversion: constants.formFieldConversionTypes.value,
        options: 'hml',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel',
          ref: valueFieldRef
        }
      });
    } else if (field?.value === constants.entity.bulkActions.setDealflowStatus) {
      fields.push({
        name: 'dealflowStatusId',
        label: 'Value',
        placeholder: 'Dealflow status',
        entity: 'bulk',
        type: constants.formFieldTypes.autocomplete,
        validation: constants.formFieldValidationTypes.dealflowStatus,
        conversion: constants.formFieldConversionTypes.value,
        options: 'dealflowStatuses',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel',
          ref: valueFieldRef
        }
      });
    } else if (field?.value === constants.entity.bulkActions.setDealLeader) {
      fields.push({
        name: 'dealLeaderId',
        label: 'Value',
        placeholder: 'Deal leader',
        entity: 'bulk',
        type: constants.formFieldTypes.user,
        validation: constants.formFieldValidationTypes.dealLeader,
        conversion: constants.formFieldConversionTypes.value,
        options: 'dealLeaders',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel',
          ref: valueFieldRef
        }
      });
    } else {
      if (field?.value?.startsWith(constants.entity.bulkActions.setClientTags)) {
        const tagGroup = universeCollection?.tagGroups.find((tg) => {
          return +tg.groupId === +field.value.split('_')[1]
        });

        fields.push({
          name: 'clientTags',
          label: 'Value',
          placeholder: tagGroup.multiselect ? 'Tags' : 'Tag',
          entity: 'bulk',
          type: constants.formFieldTypes.autocomplete,
          validation: tagGroup?.multiselect ? `${constants.formFieldValidationTypes.list}(${constants.formFieldValidationTypes.int})` :
            constants.formFieldValidationTypes.int,
          conversion: constants.formFieldConversionTypes.tag,
          options: tagGroup.tags.map((t) => ({
            label: t.value,
            value: t.tagId
          })),
          required: true,
          FormFieldProps: {
            variant: 'staticLabel',
            ref: valueFieldRef,
            multiple: tagGroup.multiselect,
            hiddenIcons: false,
            TagProps: {
              color: 'primary',
              variant: 'transparent'
            }
          }
        });
      } else if (field?.value?.startsWith(constants.entity.bulkActions.setCollectionTags)) {
        const tagGroup = collection?.tagGroups.find((tg) => {
          return +tg.groupId === +field.value.split('_')[1]
        });

        fields.push({
          name: 'collectionTags',
          label: 'Value',
          placeholder: tagGroup.multiselect ? 'Tags' : 'Tag',
          entity: 'bulk',
          type: constants.formFieldTypes.autocomplete,
          validation: tagGroup?.multiselect ? `${constants.formFieldValidationTypes.list}(${constants.formFieldValidationTypes.int})` :
            constants.formFieldValidationTypes.int,
          conversion: constants.formFieldConversionTypes.tag,
          options: tagGroup.tags.map((t) => ({
            label: t.value,
            value: t.tagId
          })),
          required: true,
          FormFieldProps: {
            variant: 'staticLabel',
            ref: valueFieldRef,
            multiple: tagGroup.multiselect,
            hiddenIcons: false,
            TagProps: {
              color: 'primary',
              variant: 'transparent'
            }
          }
        });
      }
    }

    return fields;
  }, [bulkActions, collection?.tagGroups, universeCollection?.tagGroups, field]);

  const handleChange = (field, value) => {
    onChange?.(field, value);
  };

  const handleChangeDirect = (e) => {
    const field = fields?.find((field) => field.name === e?.target?.name);
    const value = e?.target?.value;

    if (field.name === 'field') {
      setField(value);
      formRef.current?.reset();
    }
  }

  const handleSubmit = (values, actions) => {
    setSubmitting(true);

    let field = values.field.split('_')[0];
    let fieldId = values.field.split('_')[1];

    let action = {};
    action.action = field;
    if (action.action === constants.entity.bulkActions.setPersonalRelevancy) {
      action.relevancy = values.personalRelevancy;
    } else if (action.action === constants.entity.bulkActions.setExternalRelevancy) {
      action.relevancy = values.externalRelevancy;
    } else if (action.action === constants.entity.bulkActions.setClientTags) {
      action.tagGroup = {
        groupId: +fieldId,
        tags: utils.toArray(values.clientTags, true)
      };
    } else if (action.action === constants.entity.bulkActions.setCollectionTags) {
      action.tagGroup = {
        groupId: +fieldId,
        tags: utils.toArray(values.collectionTags, true)
      };
    } else if (action.action === constants.entity.bulkActions.setDealLeader) {
      action.dealLeaderId = values.dealLeaderId;
    } else if (action.action === constants.entity.bulkActions.setDealflowStatus) {
      action.dealflowStatusId = values.dealflowStatusId;
    }

    utils.asPromise(onSubmit)(action)
      .then(() => {
        onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        setError('Saving companies failed');
      })
      .finally(() => {
        actions.setSubmitting(false);
        setSubmitting(false);
      });
  };

  const handleValidating = (isValidating, isDirty, hasErrors) => {
    setDirty(isDirty);
    if (hasErrors) {
      setValidation('Please check if all fields have the correct values');
    } else {
      setValidation(null);
    }
  }

  const handleSubmitClick = () => {
    formRef.current?.submit();
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e);
  }

  const handleClose = (e, reason) => {
    if ((!submitting && !error && !dirty) || ['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      onClose?.(e, reason);
    }
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button children={'Cancel'}
              variant="text"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button disabled={submitting}
              type="submit"
              variant="contained"
              children={'Save'}
              startIcon={<Icon icon={Save}/>}
              onClick={handleSubmitClick}/>
    </React.Fragment>
  }

  return <StyledEntitiesBulkDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader title={title ?? 'Edit companies'}
                  subtitle={`For ${utils.formatNumber(count)} compan${count === 1 ? 'y' : 'ies'}`}/>
    <DialogContent>
      <InlineForm ref={formRef} fields={fields}
                  fieldData={fieldData}
                  onValidating={handleValidating}
                  onChange={handleChange}
                  onChangeDirect={handleChangeDirect}
                  onSubmit={handleSubmit} />
    </DialogContent>
    <DialogFooter className="EntitiesBulkDialog-footer"
                  info={(error ?? validation) ? <Span color="error">{error ?? validation}</Span> : null}
                  buttons={renderButtons()} />
  </StyledEntitiesBulkDialog>
});

EntitiesBulkDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  title: PropTypes.string,
  count: PropTypes.number,
  bulkActions: PropTypes.array,
  collection: PropTypes.object,
  universeCollection: PropTypes.object,
  fieldData: PropTypes.object,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func
};

export default EntitiesBulkDialog;
